import { CalendarDays, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useActionsConge, useConges, useIntentions, useSituationConges } from '@/api/hooks';
import { LIBELLE_ETAPE, LIBELLE_REGIME } from '@/api/types';
import { Chargement, ErreurBloc } from '@/components/common';
import { CartePeriode, LienConge, StatutCongeBadge } from '@/components/rh';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr } from '@/lib/utils';

/** Onglet « Mes congés » de l'espace personnel (§ 3.1 et 4.3) : droits, demandes, intentions. */
export function MesConges() {
  const situation = useSituationConges();
  const conges = useConges({ onglet: 'mes' });
  const intentions = useIntentions();
  const actions = useActionsConge();
  const [intention, setIntention] = useState({ debut: '', fin: '', commentaire: '' });
  if (situation.isLoading || conges.isLoading) return <Chargement />;
  if (situation.error || !situation.data) return <ErreurBloc erreur={situation.error} />;
  const s = situation.data;
  const ajouter = async () => {
    try {
      await actions.ajouterIntention.mutateAsync({ debut: intention.debut, fin: intention.fin, commentaire: intention.commentaire || undefined });
      setIntention({ debut: '', fin: '', commentaire: '' });
      toast.success('Congé envisagé inscrit au calendrier prévisionnel.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ajout impossible.');
    }
  };
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-2">
            Mes droits à congé annuel
            <Button asChild>
              <Link to="/rh/conges/nouvelle">
                <Plus className="size-4" /> Nouvelle demande de congé
              </Link>
            </Button>
          </CardTitle>
          <CardDescription>
            {s.regimeConge ? `${LIBELLE_REGIME[s.regimeConge]} · ` : 'Régime à confirmer par les Ressources humaines · '}
            {s.douzeMoisDeService ? 'droit ouvert (douze mois de service accomplis)' : s.droitOuvertLe ? `le droit s'ouvre le ${dateFr(s.droitOuvertLe)} ; avant, une demande est une exception approuvée par le directeur et le DRH` : "date d'engagement à renseigner par les RH"}
            {s.joursMaladieAnnee ? ` · ${s.joursMaladieAnnee} jour(s) de maladie cette année` : ''}
            {s.joursNonPrestesAnnee ? ` · ${s.joursNonPrestesAnnee} jour(s) non presté(s) cette année` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {s.periodes.length === 0 && <p className="text-sm text-muted-foreground">Aucune période de droits ouverte pour le moment.</p>}
          {s.periodes.map((p) => (
            <CartePeriode key={p.id} p={p} titre={s.courante?.id === p.id ? `Période en cours · du ${dateFr(p.debut)} au ${dateFr(p.fin)}` : undefined} />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-2">
            Mes demandes
            <Button variant="outline" asChild>
              <Link to="/rh/calendrier">
                <CalendarDays className="size-4" /> Mon calendrier
              </Link>
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!conges.data?.length ? (
            <p className="text-sm text-muted-foreground">Aucune demande.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numéro</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead>Absence</TableHead>
                  <TableHead className="text-right">Jours</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {conges.data.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <LienConge id={c.id} numero={c.numero} />
                      {c.version > 1 && <span className="ml-1 text-xs text-muted-foreground">v{c.version}</span>}
                    </TableCell>
                    <TableCell>{c.libelleCategorie}</TableCell>
                    <TableCell>
                      du {dateFr(c.dateDebut)} au {dateFr(c.dernierJour)} · reprise le {dateFr(c.dateReprise)}
                    </TableCell>
                    <TableCell className="num text-right">{c.joursPris ?? c.joursOuvrables}</TableCell>
                    <TableCell className="space-x-1">
                      <StatutCongeBadge statut={c.statut} />
                      {c.statut === 'SOUMIS' && c.etapeCourante && <Badge variant="secondary">{LIBELLE_ETAPE[c.etapeCourante]}</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Congés envisagés</CardTitle>
          <CardDescription>Intentions inscrites au calendrier prévisionnel de votre direction : elles ne valent pas autorisation de départ.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(intentions.data ?? []).map((i) => (
            <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
              <span>
                du {dateFr(i.debut)} au {dateFr(i.fin)}
                {i.commentaire ? ` · ${i.commentaire}` : ''}
              </span>
              <Button size="sm" variant="ghost" aria-label="Retirer" onClick={() => actions.retirerIntention.mutateAsync(i.id).then(() => toast.success('Intention retirée.'))}>
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap items-end gap-2" data-testid="intention">
            <Input type="date" aria-label="Début envisagé" value={intention.debut} onChange={(e) => setIntention({ ...intention, debut: e.target.value })} />
            <Input type="date" aria-label="Fin envisagée" value={intention.fin} onChange={(e) => setIntention({ ...intention, fin: e.target.value })} />
            <Input className="w-64" placeholder="Commentaire" value={intention.commentaire} onChange={(e) => setIntention({ ...intention, commentaire: e.target.value })} />
            <Button variant="outline" onClick={ajouter} disabled={!intention.debut || !intention.fin || intention.fin < intention.debut || actions.ajouterIntention.isPending}>
              Inscrire
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
